// Pure, dependency-free rules for the Entities module (las entidades con las
// que la Dirección de Extensión firma convenios). Kept apart from the React
// components and the Cloud Functions so it can be unit-tested directly.
// gfunctions/entities/_shared/entityLogic.js mirrors this logic (separate Node
// deployment, cannot import from src/), so keep the two in sync when changing a
// rule.
//
// Validation returns English error CODES, not messages: the Spanish text lives
// in ./entityLabels.js, which also renders the codes the backend returns — so a
// rule checked on both sides reads the same either way.
import {
  DEFAULT_PHONE_PREFIX,
  ENTITY_DOC_FILE_STATUS,
  ENTITY_DOC_STATUS,
  ENTITY_STATUS,
  ENTITY_TYPE,
  PHONE_TYPE,
} from "../../constants";

export const ENTITY_TYPES = Object.values(ENTITY_TYPE);
export const ENTITY_STATUSES = Object.values(ENTITY_STATUS);
export const PHONE_TYPES = Object.values(PHONE_TYPE);

// --- Helpers de texto --------------------------------------------------------

const trimmed = (value) => String(value == null ? "" : value).trim();
const trimmedLower = (value) => trimmed(value).toLowerCase();

const orNull = (value) => {
  const s = trimmed(value);
  return s === "" ? null : s;
};

// Correo laxo a propósito: sólo descarta lo que evidentemente no es un correo.
// Validar direcciones de verdad es tarea del que le escriba.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Igual que en convenios: las fechas son cadenas "YYYY-MM-DD" y no Timestamps.
// La vigencia de un documento es un hecho de calendario —vence el día que dice
// el papel—, y un Timestamp caería en un día distinto según la zona del que lo
// lea. De regalo, ordenan y se comparan lexicográficamente.
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const isValidDate = (value) => {
  if (typeof value !== "string" || !DATE_RE.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  if (isNaN(d.getTime())) return false;
  // Rechaza las formas que Date corrige en silencio (2026-02-31 → 2026-03-03).
  return d.toISOString().slice(0, 10) === value;
};

// Hoy en "YYYY-MM-DD". Se pasa como parámetro en todas las funciones que
// dependen de la fecha, para que los tests no dependan del reloj.
export const isoToday = (date = new Date()) => date.toISOString().slice(0, 10);

// --- NIT ---------------------------------------------------------------------

// El NIT es el id del documento, así que se guarda en su forma canónica: sólo
// dígitos y SIN el dígito de verificación. Si viene escrito con guion
// ("900.123.456-7") se descarta lo que va después del primer guion, porque eso
// es el DV; el DV no se guarda como lo escribieron sino que se calcula.
export const normalizeNit = (value) => {
  const raw = String(value == null ? "" : value).trim();
  const base = raw.includes("-") ? raw.slice(0, raw.indexOf("-")) : raw;
  return base.replace(/\D/g, "");
};

// Dígito de verificación del NIT, algoritmo de la DIAN: cada dígito, leído de
// derecha a izquierda, se multiplica por su peso; el residuo módulo 11 da el DV
// (y un residuo de 0 ó 1 es el DV mismo).
//
// Se calcula en vez de pedirlo escrito: es la única cifra del formulario que la
// máquina puede verificar sola, así que un NIT mal tecleado se cae aquí en lugar
// de viajar hasta la Cámara de Comercio.
const DV_WEIGHTS = [3, 7, 13, 17, 19, 23, 29, 37, 41, 43, 47, 53, 59, 67, 71];

export const verificationDigit = (nit) => {
  const digits = normalizeNit(nit);
  if (!digits || digits.length > DV_WEIGHTS.length) return null;
  let sum = 0;
  for (let i = 0; i < digits.length; i += 1) {
    // i = 0 es el dígito de MÁS a la derecha, que es donde arrancan los pesos.
    const digit = Number(digits[digits.length - 1 - i]);
    sum += digit * DV_WEIGHTS[i];
  }
  const remainder = sum % 11;
  return remainder > 1 ? 11 - remainder : remainder;
};

// Un NIT colombiano tiene entre 5 y 15 dígitos. El rango es holgado a propósito:
// las entidades viejas y los organismos internacionales no siempre traen los 9
// dígitos del formato moderno.
export const isValidNit = (value) => {
  const digits = normalizeNit(value);
  return digits.length >= 5 && digits.length <= 15;
};

// "900.123.456-7" — cómo se lee un NIT en un papel colombiano.
export const formatNit = (value) => {
  const digits = normalizeNit(value);
  if (!digits) return "";
  const dv = verificationDigit(digits);
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return dv === null ? grouped : `${grouped}-${dv}`;
};

// --- Documentos --------------------------------------------------------------

// Un documento puede venir como { files: [...] } o, en registros viejos, como un
// único archivo suelto. Se normaliza para que el resto del módulo lea siempre
// una lista. Copiado de applicantLogic: misma forma, mismo problema.
export const normalizeDoc = (entry) => {
  if (!entry || typeof entry !== "object") return { files: [] };
  if (Array.isArray(entry.files)) return entry;
  if (entry.path) return { ...entry, files: [entry] };
  return { ...entry, files: [] };
};

// Sólo los archivos que de verdad están en el bucket.
export const docFiles = (entry) => normalizeDoc(entry).files.filter((f) => f && f.path);

// VENCIDO se DERIVA, nunca se guarda: un documento se vence solo, el día que le
// toca, sin que nadie escriba nada. Un contador guardado se quedaría mintiendo
// hasta la siguiente edición. Es la misma regla que en convenios con
// "próximo a vencer".
export const isExpired = (file, today = isoToday()) =>
  !!(file && file.expiryDate && isValidDate(file.expiryDate) && file.expiryDate < today);

// Días que le quedan de vigencia al archivo. Negativo = ya venció, null = no
// vence nunca. Sólo para mostrar.
export const daysUntilExpiry = (file, today = isoToday()) => {
  if (!file || !file.expiryDate || !isValidDate(file.expiryDate)) return null;
  const end = new Date(`${file.expiryDate}T00:00:00Z`);
  const from = new Date(`${today}T00:00:00Z`);
  return Math.round((end - from) / 86400000);
};

// Estado efectivo de un archivo: el guardado, salvo que ya se haya vencido. Un
// documento vencido no sirve aunque alguien lo haya validado en su momento, así
// que EXPIRED gana sobre VALIDATED — pero nunca sobre REJECTED, que es una
// decisión humana y sigue siendo la razón por la que hay que volver a cargarlo.
export const EXPIRED = "EXPIRED";

export const fileEffectiveStatus = (file, today = isoToday()) => {
  if (!file) return ENTITY_DOC_FILE_STATUS.PENDING;
  if (file.status === ENTITY_DOC_FILE_STATUS.REJECTED) return ENTITY_DOC_FILE_STATUS.REJECTED;
  if (isExpired(file, today)) return EXPIRED;
  return file.status || ENTITY_DOC_FILE_STATUS.UPLOADED;
};

// Estado de UN documento a partir de sus archivos.
export const docStatusRollup = (entry, today = isoToday()) => {
  const files = docFiles(entry);
  if (files.length === 0) return ENTITY_DOC_FILE_STATUS.PENDING;
  const statuses = files.map((f) => fileEffectiveStatus(f, today));
  if (statuses.some((s) => s === ENTITY_DOC_FILE_STATUS.REJECTED)) {
    return ENTITY_DOC_FILE_STATUS.REJECTED;
  }
  if (statuses.some((s) => s === EXPIRED)) return EXPIRED;
  if (statuses.every((s) => s === ENTITY_DOC_FILE_STATUS.VALIDATED)) {
    return ENTITY_DOC_FILE_STATUS.VALIDATED;
  }
  return ENTITY_DOC_FILE_STATUS.UPLOADED;
};

// Cargar un archivo es una de dos cosas, y el gestor tiene que poder decir
// cuál: REEMPLAZAR uno guardado o AÑADIR otro. `replaceFileId` nombra el que se
// reemplaza y versiona ESE espacio —el anterior se va a versions[], se reusa el
// id— de modo que el documento no se duplica y la decisión anterior queda
// anulada: el reemplazo vuelve a UPLOADED. Sin él, se añade uno nuevo mientras
// quepa. Copiado de applicantLogic, sin la noción de `entryId`: los documentos
// de una entidad no cuelgan de una lista repetible.
export const resolveUploadSlot = (files, opts) => {
  const { replaceFileId = null, maxFiles = 1 } = opts || {};
  const list = Array.isArray(files) ? files : [];

  if (replaceFileId) {
    const target = list.find((f) => f && f.id === replaceFileId);
    if (!target) return { error: "UNKNOWN_REPLACE_TARGET" };
    const versions = Array.isArray(target.versions) ? [...target.versions] : [];
    if (target.path) {
      versions.push({
        path: target.path,
        hash: target.hash,
        uploadedAt: target.uploadedAt || null,
        status: target.status || null,
        expiryDate: target.expiryDate || null,
        // La fecha de expedición viaja con la versión a la que pertenece. Sin
        // ella, una versión histórica no se puede interpretar: demostrar con
        // qué RUT se respaldó una factura de hace meses es leer el archivo que
        // estaba vigente entonces, y esa respuesta es su fecha de expedición.
        issuedAt: target.issuedAt || null,
        rejectionComments: target.rejectionComments || null,
      });
    }
    return {
      fileId: target.id,
      version: (target.version || 1) + 1,
      versions,
      replaceIndex: list.indexOf(target),
    };
  }

  // maxFiles 0 = sin tope, misma convención que el catálogo de aspirantes.
  const cap = Number(maxFiles) === 0 ? Infinity : Math.max(1, Number(maxFiles) || 1);
  if (list.length >= cap) return { error: "DOC_AT_MAX_FILES" };
  return { fileId: null, version: 1, versions: [], replaceIndex: -1 };
};

// --- Catálogo de documentos por tipo de entidad ------------------------------

// Qué documentos se le exigen a ESTA entidad. Un documento sin `entityTypes`
// declarados se le pide a todas: es la misma convención de
// requiredStage1For en aspirantes ("vacío = el conjunto estándar"), y evita que
// crear un tipo de entidad nuevo deje el catálogo en blanco.
export const catalogForEntityType = (catalog, entityType) =>
  (Array.isArray(catalog) ? catalog : [])
    .filter((d) => d && d.enabled !== false)
    .filter((d) => {
      const types = Array.isArray(d.entityTypes) ? d.entityTypes.filter(Boolean) : [];
      return types.length === 0 || types.includes(entityType);
    })
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0));

export const requiredDocKeys = (catalog, entityType) =>
  catalogForEntityType(catalog, entityType)
    .filter((d) => d.required !== false)
    .map((d) => d.key);

// Estado y porcentaje de la documentación de la entidad, medidos SÓLO contra los
// documentos que a ella le aplican: exigirle a una alcaldía la cámara de
// comercio que nunca va a tener la dejaría incompleta para siempre.
export const computeDocumentation = (documents, catalog, entityType, today = isoToday()) => {
  const required = requiredDocKeys(catalog, entityType);
  const docs = documents || {};
  if (required.length === 0) {
    return { status: ENTITY_DOC_STATUS.COMPLETE, percentage: 100 };
  }
  let present = 0;
  let validated = 0;
  for (const key of required) {
    const rollup = docStatusRollup(docs[key], today);
    // Un documento rechazado o vencido no cuenta como entregado: hay que volver
    // a cargarlo.
    if (rollup === ENTITY_DOC_FILE_STATUS.UPLOADED || rollup === ENTITY_DOC_FILE_STATUS.VALIDATED) {
      present += 1;
    }
    if (rollup === ENTITY_DOC_FILE_STATUS.VALIDATED) validated += 1;
  }
  const percentage = Math.round((present / required.length) * 100);
  let status = ENTITY_DOC_STATUS.INCOMPLETE;
  if (present === 0) status = ENTITY_DOC_STATUS.PENDING;
  else if (validated === required.length) status = ENTITY_DOC_STATUS.VALIDATED;
  else if (present === required.length) status = ENTITY_DOC_STATUS.COMPLETE;
  return { status, percentage };
};

// --- Avisos de vencimiento ---------------------------------------------------

// La fecha más temprana en la que ALGÚN documento de la entidad necesita aviso,
// o null si no hay ninguno pendiente.
//
// Es el único dato derivado que se desnormaliza al documento de la entidad, y se
// hace por una limitación dura: Firestore no puede consultar un campo que vive
// dentro de documents[tipo].files[], así que sin esta fecha arriba el barrido
// diario tendría que leerse TODAS las entidades. Con ella, la consulta es
// `where("nextExpiryNoticeDate", "<=", hoy)` y sólo bajan las que toca avisar.
//
// Un archivo ya avisado lleva `expiryNoticeFor` igual a la vigencia por la que se
// avisó. Comparar contra la fecha —y no un booleano ni un timestamp— hace que el
// aviso se re-arme SOLO cuando la entidad recarga el documento con una vigencia
// nueva, que es justo lo que se le pidió que hiciera.
export const expiryNoticeDateFor = (file, docDef) => {
  if (!file || !file.expiryDate || !isValidDate(file.expiryDate)) return null;
  if (file.expiryNoticeFor === file.expiryDate) return null;
  const warnDays = Math.max(0, Number(docDef?.expiryWarningDays) || 0);
  const due = new Date(`${file.expiryDate}T00:00:00Z`);
  due.setUTCDate(due.getUTCDate() - warnDays);
  return due.toISOString().slice(0, 10);
};

export const nextExpiryNoticeDate = (documents, catalog, entityType) => {
  const docs = documents || {};
  let earliest = null;
  // Sólo los documentos que a ESTA entidad le aplican: avisar por una cámara de
  // comercio que a una alcaldía nunca se le pidió sería ruido puro.
  for (const docDef of catalogForEntityType(catalog, entityType)) {
    if (!docDef.hasExpiry) continue;
    for (const file of docFiles(docs[docDef.key])) {
      const date = expiryNoticeDateFor(file, docDef);
      if (date && (earliest === null || date < earliest)) earliest = date;
    }
  }
  return earliest;
};

// Los archivos a los que hoy les toca aviso, con la etiqueta del documento al
// que pertenecen. Lo usa el barrido para armar UN correo con todos.
export const filesDueForExpiryNotice = (documents, catalog, entityType, today = isoToday()) => {
  const docs = documents || {};
  const due = [];
  for (const docDef of catalogForEntityType(catalog, entityType)) {
    if (!docDef.hasExpiry) continue;
    for (const file of docFiles(docs[docDef.key])) {
      const date = expiryNoticeDateFor(file, docDef);
      if (date && date <= today) {
        due.push({ docKey: docDef.key, label: docDef.label || docDef.key, file });
      }
    }
  }
  return due;
};

// --- Contactos ---------------------------------------------------------------

// Una entidad tiene varios correos y varios teléfonos: el de la secretaría, el
// del contador, el fijo de la portería. Se guardan como listas de objetos, con
// UNO marcado `primary` que es el que alimenta los escalares `email` y `phone`
// de siempre — así toda la app que ya leía entity.email sigue funcionando sin
// migrar nada.
//
// Sólo se pueden AÑADIR desde la ficha (el portal o la pantalla del gestor). El
// registro público pide exactamente un correo y un celular, para que la puerta
// de verificación tenga una sola respuesta que dar.

export const genContactId = () =>
  Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 4);

// A un fijo no le llega un SMS, así que no hay forma de verificarlo. La UI usa
// esto para esconder el botón «Verificar» y mostrar la extensión en su lugar.
export const isVerifiableContact = (phone) =>
  !!phone && phone.type !== PHONE_TYPE.LANDLINE;

// Celular a E.164 (+57…). Espejo de toE164 en gfunctions/_shared/phoneToken.js:
// el número que se guarda tiene que ser el MISMO que Firebase Phone Auth
// devuelve en el token, o la comparación de la puerta de verificación falla.
export const toE164 = (raw, prefix = DEFAULT_PHONE_PREFIX) => {
  const s = String(raw == null ? "" : raw).trim();
  if (!s) return null;
  if (s.startsWith("+")) return `+${s.slice(1).replace(/\D/g, "")}`;
  const digits = s.replace(/\D/g, "").replace(/^0+/, "");
  return digits ? `${prefix}${digits}` : null;
};

const normalizeEmailAddress = (value) => trimmedLower(value);

// Un fijo se guarda tal cual lo escribieron (sin espacios): "604 444 5566" es un
// número perfectamente válido que E.164 sólo estropearía.
const normalizePhoneNumber = (value, type) =>
  type === PHONE_TYPE.LANDLINE
    ? String(value == null ? "" : value).replace(/\s+/g, " ").trim() || null
    : toE164(value);

// Exactamente uno primario, y siempre uno: si el que venía marcado desapareció,
// asciende el primero de la lista. Una lista sin primario dejaría los escalares
// `email`/`phone` en null y la entidad sin forma de contacto.
const withOnePrimary = (list) => {
  if (list.length === 0) return list;
  const at = list.findIndex((c) => c.primary);
  const primaryIndex = at === -1 ? 0 : at;
  return list.map((c, i) => ({ ...c, primary: i === primaryIndex }));
};

// El país de un teléfono, como código ISO-3166 alpha-2. Se GUARDA en vez de
// deducirse del indicativo porque el indicativo no siempre alcanza: +1 lo
// comparten 25 países, y un fijo se guarda tal como se tecleó, sin ningún
// indicativo del que deducir nada. Cualquier cosa que no sean dos letras entra
// como null.
export const normalizeCountryCode = (value) => {
  const c = String(value == null ? "" : value).trim().toUpperCase();
  return /^[A-Z]{2}$/.test(c) ? c : null;
};

export const normalizeContactEmails = (list) =>
  withOnePrimary(
    (Array.isArray(list) ? list : [])
      .map((c) => ({
        id: c?.id || genContactId(),
        address: normalizeEmailAddress(c?.address),
        // Who answers for this row. `userId` ties it to one of the entity's
        // accounts, and then the address IS that person's sign-in address;
        // `null` means an external contact, and there `label` carries the name
        // — a bare address nobody owns is what this field exists to stop.
        userId: orNull(c?.userId),
        label: orNull(c?.label),
        primary: !!c?.primary,
        // Quien no diga nada recibe los avisos: es lo que espera cualquiera que
        // se molestó en registrar un correo.
        notify: c?.notify !== false,
        verified: !!c?.verified,
        verifiedAt: c?.verifiedAt || null,
      }))
      .filter((c) => c.address)
      // Por dirección, no por id: dos filas con el mismo correo son la misma.
      .filter((c, i, all) => all.findIndex((o) => o.address === c.address) === i)
  );

export const normalizeContactPhones = (list) =>
  withOnePrimary(
    (Array.isArray(list) ? list : [])
      .map((c) => {
        const type = PHONE_TYPES.includes(c?.type) ? c.type : PHONE_TYPE.MOBILE;
        return {
          id: c?.id || genContactId(),
          number: normalizePhoneNumber(c?.number, type),
          type,
          countryCode: normalizeCountryCode(c?.countryCode),
          // La extensión sólo tiene sentido en un fijo.
          extension: type === PHONE_TYPE.LANDLINE ? orNull(c?.extension) : null,
          // Same as on an email: the account this number belongs to, or null for
          // an external contact. A member may hold several rows (one mobile plus
          // however many landlines); an external one only needs its `label`.
          userId: orNull(c?.userId),
          label: orNull(c?.label),
          primary: !!c?.primary,
          // Un fijo nace y muere sin verificar: no es verificable.
          verified: type === PHONE_TYPE.LANDLINE ? false : !!c?.verified,
          verifiedAt: type === PHONE_TYPE.LANDLINE ? null : c?.verifiedAt || null,
        };
      })
      .filter((c) => c.number)
      .filter((c, i, all) => all.findIndex((o) => o.number === c.number) === i)
  );

// Los índices planos con los que el login por OTP resuelve el NIT. Sólo entran
// los contactos VERIFICADOS: un correo sin verificar es una afirmación de quien
// escribió el formulario, y dejarlo resolver a una entidad sería entregarle esa
// ficha a cualquiera que lo teclee.
export const contactIndexes = (emails, phones) => ({
  contactEmailIndex: (emails || []).filter((c) => c.verified).map((c) => c.address),
  contactPhoneIndex: (phones || [])
    .filter((c) => c.verified && isVerifiableContact(c))
    .map((c) => c.number),
});

export const primaryContactEmail = (emails) =>
  (emails || []).find((c) => c.primary)?.address || (emails || [])[0]?.address || null;

export const primaryContactPhone = (phones) =>
  (phones || []).find((c) => c.primary)?.number || (phones || [])[0]?.number || null;

// Los correos a los que se le avisa un vencimiento. El fallback al escalar
// `email` sostiene a las entidades creadas antes de que existieran las listas.
export const notifyEmails = (entity) => {
  const list = (entity?.contactEmails || [])
    .filter((c) => c.verified && c.notify)
    .map((c) => c.address);
  if (list.length) return list;
  return entity?.email ? [entity.email] : [];
};

export const validateContacts = (emails, phones) => {
  const errors = {};
  const mails = emails || [];
  const tels = phones || [];

  if (mails.length === 0) errors.contactEmails = "EMAIL_REQUIRED";
  if (mails.some((c) => !EMAIL_RE.test(c.address))) errors.contactEmails = "INVALID_EMAIL";
  if (mails.length && !mails.some((c) => c.primary)) {
    errors.contactEmails = "PRIMARY_CONTACT_REQUIRED";
  }
  if (tels.length && !tels.some((c) => c.primary)) {
    errors.contactPhones = "PRIMARY_CONTACT_REQUIRED";
  }
  // Un celular tiene que quedar en E.164 para que Phone Auth lo pueda comparar.
  if (tels.some((c) => isVerifiableContact(c) && !/^\+\d{7,15}$/.test(c.number || ""))) {
    errors.contactPhones = "INVALID_PHONE";
  }

  // An external contact has to say WHO it is. A row with no `userId` and no
  // `label` is an address nobody answers for, which is the whole thing these
  // rules exist to stop.
  if (mails.some((c) => !c.userId && !c.label)) errors.contactEmails = "CONTACT_NAME_REQUIRED";
  if (tels.some((c) => !c.userId && !c.label)) errors.contactPhones = "CONTACT_NAME_REQUIRED";

  // One address per account, and it is that person's sign-in address. Two rows
  // pointing at the same account would make "which one do they log in with?"
  // unanswerable.
  const owners = mails.filter((c) => c.userId).map((c) => c.userId);
  if (new Set(owners).size !== owners.length) errors.contactEmails = "MEMBER_EMAIL_EXISTS";

  // A member reachable only on a landline cannot be sent a code, so they could
  // never verify anything. Landline-only is for EXTERNAL contacts; a member who
  // already has a verified mobile may add as many landlines as they like.
  const byOwner = new Map();
  for (const c of tels) {
    if (!c.userId) continue;
    byOwner.set(c.userId, (byOwner.get(c.userId) || false) || (isVerifiableContact(c) && !!c.verified));
  }
  for (const hasMobile of byOwner.values()) {
    if (!hasMobile) { errors.contactPhones = "MEMBER_MOBILE_REQUIRED"; break; }
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

// Alta, baja y cambio de primario sobre una de las listas. Devuelve
// { list } o { error }, nunca lanza: el que llama traduce el código.
//
// Borrar el primario está prohibido en vez de ascender a otro en silencio: es el
// correo por el que la entidad entra al portal, y perderlo por un clic sin haber
// nombrado al reemplazo deja a la entidad afuera.
export const applyContactChange = (list, change) => {
  const items = Array.isArray(list) ? [...list] : [];
  const { action, id } = change || {};

  if (action === "DELETE") {
    const target = items.find((c) => c.id === id);
    if (!target) return { error: "CONTACT_NOT_FOUND" };
    if (target.primary) return { error: "PRIMARY_CONTACT_REQUIRED" };
    return { list: items.filter((c) => c.id !== id) };
  }

  if (action === "SET_PRIMARY") {
    const target = items.find((c) => c.id === id);
    if (!target) return { error: "CONTACT_NOT_FOUND" };
    // Sólo un contacto verificado puede ser el primario: el escalar `email` es a
    // donde salen los avisos y por donde se entra al portal.
    if (!target.verified) return { error: "CONTACT_NOT_VERIFIED" };
    return { list: items.map((c) => ({ ...c, primary: c.id === id })) };
  }

  if (action === "ADD") {
    // Same as UPDATE: `verified` does not come in through here, the verification
    // gate writes it. Without this strip, adding a contact already marked as
    // verified put it straight into the indexes, which is exactly what those
    // indexes exist to prevent.
    const { verified, verifiedAt, ...contact } = change.contact || {};
    return { list: [...items, { ...contact, id: contact.id || genContactId() }] };
  }

  if (action === "UPDATE") {
    const at = items.findIndex((c) => c.id === id);
    if (at === -1) return { error: "CONTACT_NOT_FOUND" };
    // `verified` no se toca por aquí: lo escribe la puerta de verificación.
    const { verified, verifiedAt, ...patch } = change.contact || {};
    items[at] = { ...items[at], ...patch, id: items[at].id };
    return { list: items };
  }

  return { error: "UNKNOWN_CONTACT_ACTION" };
};

// --- Validación --------------------------------------------------------------

// Valida los campos que un gestor puede enviar. Con `partial` (una edición) sólo
// se miran las claves presentes.
//
// Devuelve { valid, errors } donde errors mapea campo → código en inglés.
export const validateEntity = (input = {}, { partial = false } = {}) => {
  const errors = {};
  const has = (key) => !partial || Object.prototype.hasOwnProperty.call(input, key);

  if (has("nit") && !isValidNit(input.nit)) errors.nit = "NIT_INVALID";

  if (has("legalName") && trimmed(input.legalName).length < 3) {
    errors.legalName = "LEGAL_NAME_REQUIRED";
  }

  if (has("entityType") && !ENTITY_TYPES.includes(input.entityType)) {
    errors.entityType = "INVALID_ENTITY_TYPE";
  }

  // Un `status` AUSENTE se acepta, igual que un correo en blanco: quien no lo
  // manda no lo está eligiendo. intakeEntity es justamente eso — el portal
  // público no puede fijar su propio estado, lo asigna la función (PENDING al
  // nacer, el que ya tenía al editar). Sin este `!= null` la validación pedía un
  // campo que el formulario público ni siquiera muestra, y TODO registro y TODO
  // guardado de la ficha se caía con INVALID_STATUS.
  if (has("status") && input.status != null && !ENTITY_STATUSES.includes(input.status)) {
    errors.status = "INVALID_STATUS";
  }

  if (has("email") && trimmed(input.email) && !EMAIL_RE.test(trimmed(input.email))) {
    errors.email = "INVALID_EMAIL";
  }

  if (has("legalRepEmail") && trimmed(input.legalRepEmail) && !EMAIL_RE.test(trimmed(input.legalRepEmail))) {
    errors.legalRepEmail = "INVALID_EMAIL";
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

// Valida la vigencia que el gestor le pone a un archivo. Se acepta vacía (hay
// documentos que no vencen) pero no una fecha inventada.
export const validateExpiryDate = (value) => {
  if (value === null || value === undefined || value === "") return { valid: true, errors: {} };
  return isValidDate(value)
    ? { valid: true, errors: {} }
    : { valid: false, errors: { expiryDate: "INVALID_EXPIRY_DATE" } };
};

// Fecha de EXPEDICIÓN del documento: cuándo lo generó quien lo emite. A
// diferencia de la de vencimiento —que DECLARA quien carga, y por eso es
// opcional—, esta se exige cuando el catálogo dice que el documento la tiene
// (`hasIssueDate`), porque es de donde otro módulo deriva la vigencia. Un
// documento registrado sin ella no podría juzgarse vigente ni vencido nunca.
//
// Una fecha FUTURA se rechaza por inconsistente en vez de guardarse: nada se
// expide mañana, y el módulo de pagos ya la trata como inválida (isRutValid).
// Mejor detenerla en el origen que conservar un valor que todo lector deba
// desconfiar.
export const validateIssueDate = (value, opts) => {
  const { required = false, today = isoToday() } = opts || {};
  const empty = value === null || value === undefined || value === "";
  if (empty) {
    return required
      ? { valid: false, errors: { issuedAt: "ISSUE_DATE_REQUIRED" } }
      : { valid: true, errors: {} };
  }
  if (!isValidDate(value)) {
    return { valid: false, errors: { issuedAt: "INVALID_ISSUE_DATE" } };
  }
  if (value > today) {
    return { valid: false, errors: { issuedAt: "ISSUE_DATE_IN_FUTURE" } };
  }
  return { valid: true, errors: {} };
};

// --- Saneamiento -------------------------------------------------------------

// Lista blanca de lo que se guarda. Es una lista blanca y no un `...input` para
// que un campo suelto del formulario —o de un cliente hecho a mano— no acabe
// dentro del documento. `documents` y `agreementCount` NO están aquí: se mueven
// por sus propias acciones.
export const sanitizeEntity = (input = {}) => {
  const nit = normalizeNit(input.nit);
  return {
    nit,
    verificationDigit: verificationDigit(nit),
    legalName: trimmed(input.legalName),
    tradeName: orNull(input.tradeName),
    entityType: input.entityType,
    sector: orNull(input.sector),
    country: trimmed(input.country) || "Colombia",
    department: orNull(input.department),
    city: orNull(input.city),
    address: orNull(input.address),
    phone: orNull(input.phone),
    email: orNull(input.email) ? trimmedLower(input.email) : null,
    website: orNull(input.website),
    legalRepName: orNull(input.legalRepName),
    // El tipo va junto al número: un «123456» sin decir si es cédula o
    // pasaporte no identifica a nadie. El valor es el texto largo que sale de
    // config/entities.idTypes, como applicants.idType.
    legalRepIdType: orNull(input.legalRepIdType),
    legalRepIdNumber: orNull(input.legalRepIdNumber),
    legalRepEmail: orNull(input.legalRepEmail) ? trimmedLower(input.legalRepEmail) : null,
    legalRepPhone: orNull(input.legalRepPhone),
    // El país del teléfono del representante, ISO-3166 alpha-2. Se guarda por la
    // misma razón que contactPhones[].countryCode: del indicativo no siempre se
    // puede deducir (+1 lo comparten 25 países) y sin él un número extranjero se
    // guardaba con el +57 de Colombia encajado a la fuerza.
    legalRepPhoneCountryCode: normalizeCountryCode(input.legalRepPhoneCountryCode),
    status: input.status || ENTITY_STATUS.ACTIVE,
    notes: orNull(input.notes),
    logoPath: orNull(input.logoPath),
  };
};

// Las listas de contacto se sanean aparte de sanitizeEntity porque no se mueven
// con el formulario: viajan por sus propias acciones y arrastran los derivados
// (`email`/`phone` primarios y los dos índices) que NUNCA se aceptan del cliente.
//
// `verified` tampoco se acepta: entra sólo por la puerta de verificación del
// backend, que es la única que vio el OTP o el token de Phone Auth. Por eso el
// que llama pasa `verifiedEmails`/`verifiedPhones` con lo que acabó de probar.
export const sanitizeContacts = (input = {}, { verifiedEmails = [], verifiedPhones = [] } = {}) => {
  const emails = normalizeContactEmails(
    (input.contactEmails || []).map((c) => ({
      ...c,
      verified: !!c.verified || verifiedEmails.includes(normalizeEmailAddress(c.address)),
    }))
  );
  const phones = normalizeContactPhones(
    (input.contactPhones || []).map((c) => ({
      ...c,
      verified: !!c.verified || verifiedPhones.includes(toE164(c.number)),
    }))
  );
  return {
    contactEmails: emails,
    contactPhones: phones,
    ...contactIndexes(emails, phones),
    email: primaryContactEmail(emails),
    phone: primaryContactPhone(phones),
  };
};
